import { Box, Button, MessageBlock } from '@deckee/deck-hand';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { telemetry } from '../../../services/backend';
import { captureEvent } from '../../../services/event-capture';
import {
  createPlan,
  updateFloatPlan,
} from '../../../services/log-trips/float-plan';
import { ROUTE_FLOAT_PLAN } from '../../../services/route/route-names';
import { transitionToNextRoute } from '../../../services/route/route-navigation';
import { AppState, useAppDispatch } from '../../../services/store';
import {
  hideAllLayers,
  hideControlPane,
  hideControlPaneReset,
} from '../../../services/store/modules/app-controls';
import {
  reset as floatPlanReset,
  reset as resetOldFloatplan,
  setId,
  setShareToken,
} from '../../../services/store/modules/float-plan';
import {
  clearActiveTripHidden,
  setActiveTripHidden,
} from '../../../services/store/modules/log-trips';
import {
  addPoint,
  removePoint,
  reset,
  selectPlanHasDiving,
  selectPlanIsLoggingOn,
  selectPoints,
  selectSelectedEmergencyContacts,
} from '../../../services/store/modules/plan';
import { PlanPoint } from '../../../services/store/modules/plan/models';
import { formatLatLon } from '../../helpers/coordinate-helpers';
import SheetLayout, { SheetBody } from '../../layouts/SheetLayout/SheetLayout';
import HeaderBar from '../../layouts/components/HeaderBar/HeaderBar';
import HeaderBarButton from '../../layouts/components/HeaderBarButton/HeaderBarButton';
import LogoSpinner from '../../shared/_deprecated/components/LogoSpinner/LogoSpinner';
import { back, close } from '../../shared/vector-images';
import useVessels from '../Vessels/hooks/use-vessels';
import PlanProgressPoint from './components/PlanProgressPoint/PlanProgressPoint';
import useMapPlanPoints from './hooks/use-map-plan-points';

const PlanDetailsPage: FC = () => {
  // This is used as the identifier for the fake "add waypoint" point
  const PLACEHOLDER_ID = 'placeholder';

  const history = useHistory();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [hasError, setError] = useState(false);
  const planPoints = useSelector(selectPoints);
  const selectedEmergencyContact = useSelector(selectSelectedEmergencyContacts);
  const currentFloatPlan = useSelector((state: AppState) => state.floatPlan);
  const isRoundTrip = currentFloatPlan.time_type !== 'arrival';
  const isLoggingOn = useSelector(selectPlanIsLoggingOn);
  const isDiving = useSelector(selectPlanHasDiving);
  const { selectedVessel: vessel, isSelectedLoggedOnVesselInvalid } =
    useVessels();

  const planPointsAndAdd = [
    ...planPoints,
    {
      id: PLACEHOLDER_ID,
      name: '',
      ordinal: planPoints[planPoints.length - 1]?.ordinal - 0.5, // Ordinal is just behind the last one
      stepDescription: '',
    },
  ];

  const firstPoint = planPoints[0];
  const lastPoint = planPoints[planPoints.length - 1];

  const hasFirstAndLastLatLon =
    firstPoint?.latitude &&
    firstPoint?.longitude &&
    lastPoint?.latitude &&
    lastPoint?.longitude;

  const hasMandatoryWaypointIfRequired =
    !isRoundTrip ||
    (isRoundTrip &&
      hasFirstAndLastLatLon &&
      planPoints.length >= 3 &&
      planPoints[1]?.latitude &&
      planPoints[1]?.longitude);

  useMapPlanPoints();

  const handleBackButton = () => {
    history.push('/maps/plan/create');
  };

  const getWaypointHeading = (point: PlanPoint): string => {
    if (point.id === firstPoint.id) {
      return 'Start point';
    }

    if (point.id === lastPoint.id) {
      return 'End point';
    }

    return 'Waypoint';
  };

  const getPointDescription = (point: PlanPoint): string | React.ReactNode => {
    if (point.name || (point.latitude && point.longitude)) {
      return (
        <Box fontWeight="bold" color="black">
          {point.name ||
            formatLatLon('coordinate', point.latitude, point.longitude)}
        </Box>
      );
    }

    if (point.id === firstPoint.id) {
      return 'Add start point';
    }

    if (point.id === lastPoint.id) {
      return 'Add end point';
    }
    return `Add waypoint ${isRoundTrip ? '' : '(optional)'}`;
  };

  const handlePlanUpdate = async () => {
    const currentPlanPoints = planPoints;
    const startPoint = currentPlanPoints[0];
    const endPoint = currentPlanPoints[currentPlanPoints.length - 1];

    const floatPlan = await updateFloatPlan({
      ...currentFloatPlan,
      watercraftId: vessel?.id,
      startPointLatLon: {
        lat: startPoint.latitude,
        lon: startPoint.longitude,
      },
      startPointName: startPoint?.name || '',
      endPointLatLon: {
        lat: endPoint.latitude,
        lon: endPoint.longitude,
      },
      endPointName: endPoint?.name || '',
      loloEnabled: isLoggingOn,
      isDiving,
      waypoints: currentPlanPoints
        .filter(
          (point) => point.id !== startPoint.id && point.id !== endPoint.id,
        )
        .map((point) => ({
          name: point.name || '',
          ordinal: point.ordinal,
          latLon: {
            lat: point.latitude,
            lon: point.longitude,
          },
        })),
      contacts: selectedEmergencyContact.map(
        ({ id, name, contactNumber, email, contactType }) => ({
          id,
          name,
          contactNumber,
          email,
          isPrimaryBoatContact: false,
          contactType,
          createdByMember: currentFloatPlan?.owner?.id,
        }),
      ),
    });

    dispatch(reset());
    dispatch(resetOldFloatplan());

    captureEvent({
      type: 'plan-details: trip plan updated',
      payload: {},
    });

    transitionToNextRoute(ROUTE_FLOAT_PLAN, {
      token: currentFloatPlan.share_token,
    });
    dispatch(floatPlanReset());

    telemetry(
      telemetry.DEBUG,
      `[CreateFloatPlanStep.handleFloatPlanUpdate] saved float plan ${JSON.stringify(
        floatPlan,
      )}`,
    );
  };

  const handlePlanCreation = async () => {
    const currentPlanPoints = planPoints;
    const startPoint = currentPlanPoints[0];
    const endPoint = currentPlanPoints[currentPlanPoints.length - 1];

    const updatedPlan = {
      ...currentFloatPlan,
      watercraftId: vessel?.id,
      loloEnabled: isLoggingOn,
      isDiving,
      startPointLatLon: {
        lat: startPoint.latitude,
        lon: startPoint.longitude,
      },
      startPointName: startPoint.name || '',
      endPointLatLon: {
        lat: endPoint.latitude,
        lon: endPoint.longitude,
      },
      endPointName: endPoint.name || '',
      waypoints: currentPlanPoints
        .filter(
          (point) => point.id !== startPoint.id && point.id !== endPoint.id,
        )
        .map((point) => ({
          name: point.name || '',
          ordinal: point.ordinal,
          latLon: {
            lat: point.latitude,
            lon: point.longitude,
          },
        })),
      contacts: selectedEmergencyContact.map(
        ({ name, contactNumber, email, contactType, id }) => ({
          id,
          name,
          contactNumber,
          email,
          isPrimaryBoatContact: false,
          contactType,
        }),
      ),
    };

    captureEvent({
      type: 'plan-details: trip plan created',
      payload: {
        tripType: updatedPlan.time_type,
      },
    });

    const floatPlan = await createPlan(updatedPlan);
    dispatch(setId(floatPlan.id));
    dispatch(setShareToken(floatPlan.shareToken));

    // Navigate to Complete Page
    history.push('/maps/plan/complete');

    telemetry(
      telemetry.DEBUG,
      `[Plan.handlePlanCreate] saved plan ${JSON.stringify(floatPlan)}`,
    );
  };

  const handlePlanComplete = async () => {
    try {
      setLoading(true);
      if (currentFloatPlan.id) {
        // Update the existing plan
        await handlePlanUpdate();
      } else {
        // Create a new plan
        await handlePlanCreation();
      }
    } catch (err) {
      setError(true);
      telemetry(
        telemetry.ERROR,
        `[Plan.handlePlanCreate] Error retrieving ${JSON.stringify(err)}`,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleLogOnButton = async (isLogOn = false) => {
    captureEvent({
      type: 'plan-details: confirm trip plan',
      payload: {
        isLogOn,
      },
    });

    await handlePlanComplete();
  };

  const handleOnCardClick = (pointId: string) => {
    captureEvent({
      type: 'plan-details: click choose location',
      payload: {},
    });
    let id = pointId;

    // A point needs to be created if the point selected was the placeholder
    if (pointId === PLACEHOLDER_ID) {
      id = uuidv4();
      dispatch(addPoint(id));
    }
    history.push(`/maps/plan/details/search-location/${id}`);
  };

  const handleOnDeletePointClick = (pointId: string) => {
    captureEvent({
      type: 'plan-details: click delete point',
      payload: {},
    });
    dispatch(removePoint(pointId));
  };

  const getStepType = (point: PlanPoint) => {
    const { ordinal } = point;

    if (point.id === PLACEHOLDER_ID) {
      return 'none';
    }

    if (ordinal === 0) {
      return 'start';
    }
    if (
      planPointsAndAdd &&
      ordinal === planPointsAndAdd[planPointsAndAdd.length - 1].ordinal
    ) {
      return 'end';
    }

    return 'middle';
  };

  const showDeleteButton = (point: PlanPoint) => {
    if (
      point.ordinal === 0 ||
      point.ordinal === lastPoint.ordinal ||
      point.id === PLACEHOLDER_ID
    ) {
      return false;
    }

    return true;
  };

  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      dispatch(setActiveTripHidden());
      dispatch(hideAllLayers(true));
      dispatch(hideControlPane());
    }
    return () => {
      isMounted = false;
      dispatch(clearActiveTripHidden());
      dispatch(hideControlPaneReset());
      dispatch(hideAllLayers(false));
      telemetry(telemetry.DEBUG, '[PlanDetailsPage.useEffect] unmounted');
    };
  }, [dispatch]);

  const headerLeftButtons = [
    <HeaderBarButton
      key="button-back"
      ariaLabel="back"
      icon={back()}
      onClick={handleBackButton}
    />,
  ];
  const headerRightButtons = [
    <HeaderBarButton
      key="button-close"
      ariaLabel="close"
      icon={close()}
      onClick={handleBackButton}
    />,
  ];

  return (
    <SheetLayout
      heading="Plan details"
      renderHeaderBar={(heading) => (
        <HeaderBar
          centerNode={heading}
          leftNode={headerLeftButtons}
          rightNode={headerRightButtons}
        />
      )}
      headingPosition="center"
      maxViewHeight="50"
      showMenuBar={false}
    >
      <SheetBody overflow="auto">
        {hasError && (
          <MessageBlock type="error">
            Unable to create your plan, please try again.
          </MessageBlock>
        )}
        {loading && <LogoSpinner />}
        {!loading && (
          <>
            {planPointsAndAdd &&
              planPointsAndAdd.length > 0 &&
              planPointsAndAdd
                .sort((a, b) => a.ordinal - b.ordinal)
                .map((point) => (
                  <PlanProgressPoint
                    key={`point-${point.id}`}
                    id={point.id}
                    useEllipsis
                    heading={getWaypointHeading(point)}
                    description={getPointDescription(point)}
                    stepNumber={
                      point.id !== PLACEHOLDER_ID
                        ? point?.stepDescription
                        : undefined
                    }
                    stepType={getStepType(point)}
                    onCardClick={() => handleOnCardClick(point.id)}
                    onDeleteButtonClick={() =>
                      handleOnDeletePointClick(point.id)
                    }
                    isOnDeleteButtonVisible={showDeleteButton(point)}
                  />
                ))}

            <Button.Group wide pt="3">
              <Button
                disabled={
                  !hasFirstAndLastLatLon ||
                  !hasMandatoryWaypointIfRequired ||
                  isSelectedLoggedOnVesselInvalid
                }
                onClick={() => handleLogOnButton(isLoggingOn)}
              >
                Confirm
              </Button>
            </Button.Group>
          </>
        )}
      </SheetBody>
    </SheetLayout>
  );
};

export default PlanDetailsPage;
